'use client';

import Image from 'next/image';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModalContext } from '@/app/context/QuickViewModalContext';
import { addItemToCart } from '@/redux/features/cart-slice';
import { addItemToWishlist } from '@/redux/features/wishlist-slice';
import type { RootState } from '@/redux/store';

type Props = {
  /** The domain's price policy (Core, via the shell): false = this modal renders no price. Default: shown. */
  showPrice?: boolean;
};

export function QuickViewModal({ showPrice = true }: Props) {
  const dispatch = useDispatch();
  const { isModalOpen, closeModal } = useModalContext();
  const product = useSelector((state: RootState) => state.quickViewReducer.value);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isModalOpen, closeModal]);

  if (!isModalOpen || !product?.id) {
    return null;
  }

  const image = product.imgs?.previews?.[0] || product.img || '/onlineexpotheme/content/images/slide01-min.jpg';

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
      <div
        className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-3"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={closeModal}
          className="absolute right-4 top-4 z-10 rounded-full border border-gray-3 bg-white px-3 py-1 text-sm font-semibold text-dark hover:text-primary"
        >
          X
        </button>

        <div className="grid gap-0 md:grid-cols-2">
          <div className="relative min-h-[320px] bg-gray-1">
            <Image
              src={image}
              alt={product.title}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
            />
          </div>

          <div className="space-y-4 p-6 md:p-8">
            <p className="text-custom-xs font-semibold uppercase tracking-wide text-primary">Quick View</p>
            <h3 className="heading-5 text-dark">{product.title}</h3>
            <p className="text-sm text-dark-4">{'★'.repeat(4)}☆ ({product.reviews || 0})</p>

            {showPrice ? (
              <div className="flex items-center gap-3">
                <span className="text-2xl font-semibold text-primary">${product.discountedPrice.toFixed(2)}</span>
                <span className="text-sm text-dark-4 line-through">${product.price.toFixed(2)}</span>
              </div>
            ) : null}

            <p className="text-custom-sm text-dark-3">
              Premium selection for architectural and interior projects. Fast quote and global shipping support.
            </p>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                onClick={() =>
                  dispatch(
                    addItemToCart({
                      id: product.id,
                      title: product.title,
                      price: product.price,
                      discountedPrice: product.discountedPrice,
                      quantity: 1,
                      imgs: product.imgs,
                    })
                  )
                }
                className="rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-dark"
              >
                Add to Cart
              </button>
              <button
                type="button"
                onClick={() =>
                  dispatch(
                    addItemToWishlist({
                      id: product.id,
                      title: product.title,
                      price: product.price,
                      discountedPrice: product.discountedPrice,
                      quantity: 1,
                      imgs: product.imgs,
                    })
                  )
                }
                className="rounded-lg border border-gray-3 bg-white px-4 py-3 text-sm font-semibold text-dark transition-colors hover:border-primary hover:text-primary"
              >
                Add to Wishlist
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
